/**
 * 同意書セクション レンダラー (共通)。
 *
 * v3.2.26: エディター画面のプレビューと公開画面の両方で利用。
 *
 * 機能:
 *   - heading / paragraph / bullet_list (新形式) / numbered_list (新形式)
 *   - callout (info/warning/danger) / divider / image
 *   - signature: 親コンポーネントが専用フォーム UI を描画 (ここでは label のみ)
 *   - consent_check: チェックボックス + 文言、preview モードでは disabled
 *
 * 旧形式 (items: string[]) は **サポートしない** (移行スクリプト後の新形式のみ)。
 * 移行漏れがあった場合は空表示になる。
 */
import type { ConsentListItem, ConsentSection } from '@goenhub/shared';

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function asListItems(v: unknown): ConsentListItem[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((it): ConsentListItem | null => {
      if (typeof it === 'string') {
        // 移行漏れ用フォールバック (旧形式 string)
        return { title: it };
      }
      if (
        it &&
        typeof it === 'object' &&
        typeof (it as { title?: unknown }).title === 'string'
      ) {
        const obj = it as Partial<ConsentListItem>;
        return {
          title: obj.title ?? '',
          description:
            typeof obj.description === 'string' ? obj.description : undefined,
          children: Array.isArray(obj.children)
            ? obj.children.filter((c): c is string => typeof c === 'string')
            : undefined,
        };
      }
      return null;
    })
    .filter((x): x is ConsentListItem => x !== null);
}

interface Props {
  section: ConsentSection;
  /**
   * consent_check のチェック状態 (公開画面で受け取り入力可)。
   * 未指定なら preview モードとして disabled でチェックボックス表示。
   */
  checked?: boolean;
  /** チェック変更ハンドラ。未指定なら preview モード。 */
  onCheckChange?: (next: boolean) => void;
  /**
   * 公開ページ風スタイル (白背景) を使う。
   * false (既定) は editor preview 風 (var(--*) テーマ依存)。
   */
  publicTheme?: boolean;
}

function ListItems({
  items,
  ordered,
  publicTheme,
}: {
  items: ConsentListItem[];
  ordered: boolean;
  publicTheme: boolean;
}) {
  const Tag = ordered ? 'ol' : 'ul';
  const listClass = ordered
    ? 'list-decimal pl-5 space-y-2 my-2 text-sm'
    : 'list-disc pl-5 space-y-2 my-2 text-sm';
  const descClass = publicTheme
    ? 'text-xs text-gray-600 mt-0.5'
    : 'text-xs text-[var(--text-muted)] mt-0.5';
  const childListClass = ordered
    ? 'list-[lower-alpha] pl-5 mt-1 space-y-0.5 text-sm'
    : 'list-[circle] pl-5 mt-1 space-y-0.5 text-sm';

  return (
    <Tag className={listClass}>
      {items
        .filter((it) => it.title.trim().length > 0)
        .map((it, i) => {
          const desc = it.description?.trim();
          const children = (it.children ?? []).filter((c) => c.trim().length > 0);
          return (
            <li key={i}>
              <span className="font-medium">{it.title}</span>
              {desc && <div className={descClass}>{desc}</div>}
              {children.length > 0 && (
                <ul className={childListClass}>
                  {children.map((c, ci) => (
                    <li key={ci}>{c}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
    </Tag>
  );
}

export function ConsentSectionRenderer({
  section,
  checked,
  onCheckChange,
  publicTheme = false,
}: Props) {
  const config = section.config ?? {};

  switch (section.type) {
    case 'heading': {
      const text = asString(config.text);
      if (!text) return null;
      return <h2 className="text-lg font-bold mt-6 mb-2">{text}</h2>;
    }

    case 'paragraph': {
      const text = asString(config.text);
      if (!text) return null;
      return (
        <p className="my-2 whitespace-pre-wrap leading-relaxed text-sm">
          {text}
        </p>
      );
    }

    case 'bullet_list': {
      const items = asListItems(config.items);
      if (items.length === 0) return null;
      return <ListItems items={items} ordered={false} publicTheme={publicTheme} />;
    }

    case 'numbered_list': {
      const items = asListItems(config.items);
      if (items.length === 0) return null;
      return <ListItems items={items} ordered={true} publicTheme={publicTheme} />;
    }

    case 'callout': {
      const text = asString(config.text);
      if (!text) return null;
      const tone = asString(config.tone) || 'info';
      const toneClass =
        tone === 'danger'
          ? 'bg-red-50 border-red-300 text-red-900'
          : tone === 'warning'
            ? 'bg-amber-50 border-amber-300 text-amber-900'
            : 'bg-blue-50 border-blue-300 text-blue-900';
      return (
        <div
          className={`my-3 p-3 border-l-4 rounded-r text-sm whitespace-pre-wrap ${toneClass}`}
        >
          {text}
        </div>
      );
    }

    case 'divider':
      return <hr className="my-4 border-gray-300" />;

    case 'image': {
      const url = asString(config.storagePath);
      const alt = asString(config.alt);
      if (!url) return null;
      return (
        <figure className="my-4">
          <img
            src={url}
            alt={alt}
            className="max-w-full h-auto mx-auto rounded"
          />
          {alt && (
            <figcaption className="text-center text-xs text-gray-500 mt-1">
              {alt}
            </figcaption>
          )}
        </figure>
      );
    }

    case 'consent_check': {
      const statement =
        asString(config.statement) ||
        '上記の内容に同意して申し込みをします。';
      const isPreview = typeof onCheckChange !== 'function';
      return (
        <label
          className={`my-3 flex items-start gap-2 cursor-pointer rounded p-2.5 border ${
            checked
              ? 'border-green-400 bg-green-50'
              : 'border-gray-300 bg-gray-50'
          }`}
        >
          <input
            type="checkbox"
            className="mt-0.5"
            checked={Boolean(checked)}
            disabled={isPreview}
            onChange={(e) => onCheckChange?.(e.target.checked)}
          />
          <span className="text-sm">{statement}</span>
        </label>
      );
    }

    case 'signature':
      // 親コンポーネントが専用フォーム UI を描画するため、ここではマーカーのみ
      return null;

    default:
      return null;
  }
}
